import React from 'react';
import Box from '@cloudscape-design/components/box';
import StatusIndicator from '@cloudscape-design/components/status-indicator';

/**
 * The possible speaker states for the SpeakingIndicator component.
 */
export type SpeakerState = 'user' | 'ai' | 'none';

/**
 * Props for the SpeakingIndicator component.
 */
export interface SpeakingIndicatorProps {
  /** The current speaker state: 'user', 'ai', or 'none' */
  speaker: SpeakerState;
}

/**
 * Configuration for each speaker state's visual representation.
 */
interface SpeakerConfig {
  type: 'success' | 'info' | 'stopped';
  label: string;
  description: string;
}

/**
 * Maps speaker states to their visual configuration.
 */
const SPEAKER_CONFIG: Record<SpeakerState, SpeakerConfig> = {
  user: {
    type: 'success',
    label: 'You are speaking',
    description: 'Your microphone is active',
  },
  ai: {
    type: 'info',
    label: 'AI is speaking',
    description: 'AI assistant is responding',
  },
  none: {
    type: 'stopped',
    label: 'Waiting',
    description: 'No one is speaking',
  },
};

/**
 * SpeakingIndicator Component
 *
 * A visual indicator component that shows who is currently speaking
 * during a voice training session.
 *
 * Features:
 * - Displays different visual states for 'user', 'ai', and 'none'
 * - Uses Cloudscape StatusIndicator for consistent styling
 * - Shows descriptive labels for each state
 * - Accessible with appropriate ARIA attributes
 *
 * @validates Requirements 8.3 - The page shall display a visual indicator showing who is currently speaking (user or AI)
 */
export function SpeakingIndicator({ speaker }: SpeakingIndicatorProps): React.ReactElement {
  const config = SPEAKER_CONFIG[speaker];

  return (
    <Box
      data-testid="speaking-indicator"
      textAlign="center"
      padding="s"
    >
      <Box data-testid="speaker-status">
        <StatusIndicator type={config.type}>
          {config.label}
        </StatusIndicator>
      </Box>
      <Box
        variant="small"
        color="text-body-secondary"
        margin={{ top: 'xxs' }}
        data-testid="speaker-description"
      >
        {config.description}
      </Box>
    </Box>
  );
}

export default SpeakingIndicator;
